// @ts-check
import { gql, useMutation, useQuery } from '@apollo/client';
import { Plural, Trans } from '@lingui/macro';
import {
  Box,
  Button,
  ChevronRightIcon,
  Flex,
  Link,
  SemiTitle,
  Text,
  Title,
} from '~/design-system';
import CompaniesList from '../CompanyList/CompaniesList';
import CompaniesListItem from '../CompanyList/CompaniesListItem';
// import useLink from '../useLink';
import useUrlQuery from '../useUrlQuery';

/**
 * The view. The props are passed in from useSearchCompany - all the logic resides there.
 * @param {ReturnType<useSearchCompany>} props
 * @returns
 */
export default function SearchCompany({
  data,
  loading,
  company,
  onCreateCompany,
  onSelectCompany,
}) {
  if (loading) return <FinalCheck />;

  const unsortedPossibleDuplicates = data?.companies?.nodes;

  // Put active accounts first
  const sortedPossibleDuplicates = []
    .concat(unsortedPossibleDuplicates)
    .sort((a, b) => {
      return !a?.logo && b?.logo ? 1 : -1;
    });

  // If no results, redirect to creation page
  if (sortedPossibleDuplicates.length === 0) {
    return <NewCompanyInfo onCreateCompany={onCreateCompany} />;
  }

  return (
    <Flex direction="column">
      <NewCompanyInfo sx={{ marginBottom: 4 }} />
      <PossibleMatches
        companies={sortedPossibleDuplicates}
        searchData={company}
        onCreateCompany={onCreateCompany}
        onSelectCompany={onSelectCompany}
      />
    </Flex>
  );
}

/**
 *
 * @param {object} props
 * @param {function} [props.onCreateCompany]
 * @returns
 */
function NewCompanyInfo({ onCreateCompany, ...rest }) {
  const { name, email, website, country } = useUrlQuery();
  return (
    <Flex direction="column" {...rest}>
      <Title sx={{ marginBottom: 3, textAlign: 'center' }}>
        Company details
      </Title>
      <SemiTitle sx={{ padding: 0, marginTop: 3, textAlign: 'center' }}>
        {name}
      </SemiTitle>

      <Text sx={{ textAlign: 'center' }}>{email}</Text>
      <Text sx={{ textAlign: 'center' }}>{country}</Text>
      <Text sx={{ textAlign: 'center' }}>{website}</Text>
      <Flex
        align="center"
        justify="center"
        sx={{ marginTop: 3, marginBottom: 3 }}
      >
        {onCreateCompany && (
          <Button onClick={onCreateCompany} variant="primary">
            <Text>
              <Trans>Create this company</Trans>
            </Text>
          </Button>
        )}
      </Flex>
    </Flex>
  );
}

function PossibleMatches({
  companies,
  searchData,
  onCreateCompany,
  onSelectCompany,
}) {
  return (
    <Flex direction="column" sx={{ width: ['100vw', '80vw', 450] }}>
      <Title sx={{ marginBottom: 2, textAlign: 'center' }}>
        Possible matches found
      </Title>
      <Text sx={{ marginTop: 2, textAlign: 'center' }}>
        <Plural
          value={companies.length}
          one={
            'We found an existing company that appears to match your information. Is this company that you are looking for?'
          }
          other={
            'We found several companies that appear to match your information. Are any of these companies the one that you are looking for?'
          }
        />
      </Text>

      <CompaniesList
        sx={{
          marginTop: 3,
          marginBottom: 5,
          borderTop: 1,
          borderColor: 'border',
          overflowY: 'scroll',
        }}
      >
        {companies.map((company) => (
          <CompaniesListItem
            company={company}
            onSelectCompany={onSelectCompany}
          />
        ))}
        <Link onClick={onCreateCompany} variant="text">
          <Flex
            gap={3}
            align="center"
            sx={{
              paddingLeft: 3,
              paddingRight: 1,
              borderBottom: 1,
              borderColor: 'border',
              minHeight: 74,
              color: 'black',
              ':hover': {
                backgroundColor: 'light',
                textDecoration: 'none',
              },
            }}
          >
            <Box sx={{ width: 80, marginRight: 2, flexShrink: 0 }} />
            <Box sx={{ flexGrow: 1 }}>
              <Text>
                <Trans>
                  None of these are my company. Create a workspace for{' '}
                  {searchData.name}.
                </Trans>
              </Text>
            </Box>
            <ChevronRightIcon sx={{ marginRight: 3 }} />
          </Flex>
        </Link>
      </CompaniesList>
    </Flex>
  );
}

function FinalCheck() {
  return (
    <Flex direction="column" sx={{ width: ['100vw', '80vw', 450] }}>
      <Title sx={{ marginBottom: 2, textAlign: 'center' }}>
        Searching one more time
      </Title>
      <Text sx={{ marginTop: 2, textAlign: 'center' }}>
        Based on the detailed information you've provided, we're doing one last
        search to find your company on Bottlebooks.
      </Text>
    </Flex>
  );
}

export function useSearchCompany() {
  const inviteCompany = useInviteCompany();
  const company = useUrlQuery();
  const { name, email, website, country } = company;
  const { data, loading } = useQuery(query, {
    variables: {
      filter: { name, email, website, country: { eq: country }, exact: true },
    },
  });

  return {
    loading,
    data,
    company,
    inviteCompany,
  };
}

function useInviteCompany() {
  const [_inviteCompany] = useMutation(INVITE_COMPANY);
  async function inviteCompany(
    { collectionId, name, website, email, country, owner, companyType },
    skipVerification
  ) {
    return await _inviteCompany({
      variables: {
        input: {
          collectionId,
          name,
          website,
          email,
          country,
          owner,
          companyType,
          skipVerification,
        },
      },
    });
  }
  return inviteCompany;
}

const query = gql`
  query searchCompanies($filter: CompanyFilterInput!) {
    companies(filter: $filter) {
      totalCount
      nodes {
        brandId
        ... on Producer {
          producerId
        }
        name
        street
        city
        postalCode
        stateProvince
        country
        logo {
          fixed(height: 80, width: 80, crop: PAD, fetch_format: AUTO) {
            src
            srcSet
          }
        }
      }
    }
  }
`;

const INVITE_COMPANY = gql`
  mutation inviteCompany($input: InviteCompanyInput!) {
    result: inviteCompany(input: $input) {
      company {
        brandId
        email
      }
      registrationId
      code
      success
      message
      possibleDuplicates {
        brandId
        email
      }
    }
  }
`;
